import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { List } from "lucide-react";
import { SavedViewListItem, useSavedViews } from "@/hooks/saved-views";
import { useSavedViewManagement } from "Components/SavedViews/useSavedViewManagement";
import { SavedViewMenu } from "Components/SavedViews/SavedViewMenu";

interface SavedViewsPanelProps {
    dashboardId: number;
    /** Applies a saved view to the live dashboard (re-points the embedded viz). */
    onApply: (view: SavedViewListItem) => void;
    /**
     * Captures the current Tableau URL. When provided, enables the in-dashboard
     * "Update with Current State" action; omit it on the home page panel.
     */
    getCurrentUrl?: () => Promise<string | undefined>;
}

/**
 * Toolbar "Views" button + dropdown panel listing the saved views for the
 * current dashboard. Selecting a view applies it in-place; each row has a
 * context menu (Apply, Rename, Update with Current State, Share, Remove).
 */
export function SavedViewsPanel({ dashboardId, onApply, getCurrentUrl }: SavedViewsPanelProps) {
    const [open, setOpen] = useState(false);
    // Fetch lazily on first open, then keep the subscription alive for revalidation.
    const [hasOpened, setHasOpened] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const { data: views, error, isLoading } = useSavedViews(dashboardId, { enabled: hasOpened });
    const mgmt = useSavedViewManagement({ getCurrentUrl });

    // Close the panel on outside click — but keep it open while a modal is up
    // (modals portal to <body>, so their clicks land outside the panel).
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            if (mgmt.hasModalOpen) return;
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open, mgmt.hasModalOpen]);

    const toggleOpen = () => {
        setHasOpened(true);
        setOpen((prev) => !prev);
    };

    const handleApply = (view: SavedViewListItem) => {
        onApply(view);
        setOpen(false);
    };

    return (
        <div className="saved-views-control" ref={wrapperRef}>
            <Button
                id="viewsButton"
                data-test-id="dashboard-views-btn"
                className="views-btn"
                variant="outline"
                onClick={toggleOpen}
            >
                <List size={16} />
                Views
            </Button>

            {open && (
                <div className="saved-views-panel" data-test-id="saved-views-panel">
                    <div className="saved-views-panel__header">Saved views</div>

                    {isLoading && (
                        <div className="saved-views-panel__state">
                            <Spinner animation="border" size="sm" /> Loading…
                        </div>
                    )}

                    {!isLoading && error && (
                        <div className="saved-views-panel__state text-danger">Could not load saved views.</div>
                    )}

                    {!isLoading && !error && views && views.length === 0 && (
                        <div className="saved-views-panel__state" data-test-id="saved-views-empty">
                            No saved views for this dashboard yet.
                        </div>
                    )}

                    {!isLoading && !error && views && views.length > 0 && (
                        <ul className="saved-views-panel__list">
                            {views.map((view) => (
                                <li className="saved-views-panel__row" key={view.ruuid}>
                                    <button
                                        type="button"
                                        className="saved-views-panel__name"
                                        data-test-id={`saved-view-apply-${view.ruuid}`}
                                        title={view.description || view.name}
                                        onClick={() => handleApply(view)}
                                    >
                                        {view.name}
                                    </button>

                                    {mgmt.updatingId === view.ruuid && <Spinner animation="border" size="sm" />}

                                    <SavedViewMenu
                                        view={view}
                                        onApply={handleApply}
                                        onRename={mgmt.openRename}
                                        onUpdate={mgmt.updateWithCurrentState}
                                        canUpdate={mgmt.canUpdate}
                                        onShare={mgmt.openShare}
                                        onRemove={mgmt.openRemove}
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {mgmt.modals}
        </div>
    );
}
